import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Download, Lock, Printer, ArrowRight, FileText } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";

const REDLINES = [
  { n: "01", title: "Cap exclusivity narrowly", risk: "Six-month category lockouts kill 3–4 follow-on deals.", redline: "Limit exclusivity to 30 days unless premium-priced. Define 'category' tightly — not 'all skincare', but 'mineral SPF moisturizers ≥ $30 retail'." },
  { n: "02", title: "Usage rights end when payment ends", risk: "Brands repurpose your face into paid ads for months after the post is live.", redline: "Any paid-media usage (whitelisting, dark posts, OOH) requires a separate license at a separate fee. Default cap: 30 days post-publish." },
  { n: "03", title: "Cap approval rounds", risk: "Open-ended approvals delay payment and re-cut your creative until it's lifeless.", redline: "Two rounds maximum, tied to specific milestones: script and final cut. Brand has 3 business days per round or approval is deemed given." },
  { n: "04", title: "Mutual morality clause", risk: "Brand can fire you for reputational harm — you have no reciprocal exit.", redline: "If the brand can terminate for 'reputational harm,' add a mirror clause letting you terminate for the brand's harm (lawsuits, boycotts, public scandals)." },
  { n: "05", title: "Payment terms = Net-30 from invoice", risk: "Net-60 from publish puts payment 90+ days after work is done.", redline: "Net-30 from invoice (not publish). Late fees: 1.5%/month. 50% deposit on contracts over $10k." },
  { n: "06", title: "Kill fees ≥ 50%", risk: "Brand cancels after you've shot — you eat the production cost.", redline: "50% kill fee if cancelled after pre-production starts. 100% if cancelled after delivery." },
  { n: "07", title: "FTC disclosure indemnity", risk: "Brand mandates non-compliant ad copy → FTC comes after YOU.", redline: "Brand indemnifies you against any FTC, state AG, or platform action arising from required disclosures or brand-mandated claims." },
  { n: "08", title: "Whitelisting & dark-post scope", risk: "Brand boosts your content from accounts you've never approved, in markets you've never opted into.", redline: "List every ad account (Meta/TikTok/YouTube) authorized to boost. Geographic + duration caps. Termination of access on contract end." },
  { n: "09", title: "Never guarantee performance", risk: "Performance guarantees create a near-unlimited liability surface.", redline: "Zero guarantees of impressions, views, conversions, or sales. Best-efforts language only." },
  { n: "10", title: "Governing law in YOUR state", risk: "Brand HQ states are chosen because their courts favor brands.", redline: "Disputes governed by the law of your state of residence/operation. Mandatory mediation before litigation. Attorney fees to prevailing party." },
];

const STORAGE_KEY = "uc_brand_deal_unlock";

export default function BrandDealChecklist() {
  const [email, setEmail] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1") {
      setUnlocked(true);
    }
    document.title = "Brand Deal Redline Checklist — Upload Counsel";
    return () => { document.title = "Upload Counsel"; };
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email.trim(), "brand-deal-checklist");
      localStorage.setItem(STORAGE_KEY, "1");
      setUnlocked(true);
      toast.success("Checklist unlocked. We've added you to The Brief.");
    } catch (err) {
      toast.error("Could not unlock. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-brand-deal-checklist">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 pt-24 pb-12 md:pt-32">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-[#D9B36C]">
            ← Back to blog
          </Link>
          <p className="mt-8 text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Free Resource · Lead Magnet</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl font-extrabold text-white tracking-tight max-w-3xl">
            The Brand Deal Redline Checklist.
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Ten contract redlines every creator should demand before signing a brand deal — with the exact language risks and the exact replacement asks. Print it. Use it on every contract.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-slate-400">
            <span className="inline-flex items-center gap-2"><FileText size={16} className="text-[#D9B36C]" /> 10 redlines, plain-English</span>
            <span className="inline-flex items-center gap-2"><Printer size={16} className="text-[#D9B36C]" /> Print-friendly</span>
            <span className="inline-flex items-center gap-2"><Lock size={16} className="text-[#D9B36C]" /> Free with email</span>
          </div>
        </div>
      </section>

      {/* GATE or CHECKLIST */}
      <section className="relative pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {!unlocked ? (
            <div data-testid="checklist-gate" className="rounded-3xl border border-[#272B36] bg-gradient-to-br from-[#14161D] to-[#121419] p-8 md:p-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D9B36C]/40 bg-[#D9B36C]/10 text-[#D9B36C] text-xs font-semibold uppercase tracking-widest">
                <Lock size={12} /> Unlock the checklist
              </div>
              <h2 className="mt-5 font-display text-3xl font-bold text-white tracking-tight">Drop your email. Get the checklist instantly.</h2>
              <p className="mt-3 text-slate-300 leading-relaxed">
                You'll also get The Brief — one short creator-economy legal read per week. No spam. Unsubscribe in one click.
              </p>
              <form onSubmit={unlock} className="mt-6 flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@channel.com"
                  data-testid="checklist-email-input"
                  className="flex-1 bg-[#1A1D25] border border-[#272B36] rounded-full px-5 py-3.5 text-white text-sm placeholder:text-slate-500 outline-none focus:border-[#D9B36C] focus:ring-1 focus:ring-[#D9B36C] transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="checklist-unlock-submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#D9B36C] text-black font-semibold px-7 py-3.5 rounded-full transition-all disabled:opacity-50"
                >
                  {loading ? "Unlocking…" : (<>Unlock checklist <ArrowRight size={16} /></>)}
                </button>
              </form>
              <p className="mt-4 text-xs text-slate-500">
                By submitting, you agree to our <Link to="/legal/privacy" className="text-[#D9B36C] underline">Privacy Policy</Link>. The checklist is general information, not legal advice — see the <Link to="/legal/disclaimer" className="text-[#D9B36C] underline">disclaimer</Link>.
              </p>
            </div>
          ) : (
            <div data-testid="checklist-content">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-8 print:hidden">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D9B36C]/40 bg-[#D9B36C]/10 text-[#D9B36C] text-xs font-semibold uppercase tracking-widest">
                  <CheckCircle2 size={12} /> Unlocked
                </div>
                <button
                  onClick={() => window.print()}
                  data-testid="checklist-print-btn"
                  className="inline-flex items-center gap-2 border border-[#272B36] text-white font-medium px-5 py-2.5 rounded-full hover:bg-white/5 text-sm"
                >
                  <Printer size={14} /> Print / Save as PDF
                </button>
              </div>

              <div className="rounded-3xl border border-[#272B36] bg-[#121419] p-8 md:p-10 print:bg-white print:text-black print:border-0">
                <p className="font-mono text-xs text-[#D9B36C] tracking-widest print:text-black">UPLOAD COUNSEL · uploadlawyer.com</p>
                <h2 className="mt-2 font-display text-3xl font-bold text-white tracking-tight print:text-black">The Brand Deal Redline Checklist</h2>
                <p className="mt-2 text-slate-400 print:text-gray-600 text-sm">Ten contract redlines every creator should demand. Use this as a working sheet — circle the redlines that apply to your current deal.</p>

                <ol className="mt-10 space-y-7">
                  {REDLINES.map((r) => (
                    <li key={r.n} data-testid={`redline-${r.n}`} className="rounded-2xl border border-[#272B36] bg-[#0B0C10] p-5 md:p-6 print:border-gray-300 print:bg-white">
                      <div className="flex items-start gap-4">
                        <span className="font-mono text-[#D9B36C] text-sm shrink-0 print:text-black">{r.n}</span>
                        <div className="flex-1">
                          <p className="font-display font-semibold text-white text-lg leading-tight print:text-black">{r.title}</p>
                          <div className="mt-3 grid sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold print:text-gray-600">Risk if you don't redline</p>
                              <p className="mt-1 text-slate-300 leading-relaxed print:text-gray-800">{r.risk}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-[#D9B36C] font-semibold print:text-black">Your redline</p>
                              <p className="mt-1 text-slate-200 leading-relaxed print:text-black">{r.redline}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="mt-10 rounded-2xl border border-[#272B36] bg-gradient-to-br from-[#14161D] to-[#121419] p-6 print:bg-gray-50 print:border-gray-300">
                  <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C] print:text-black">Active deal in front of you?</p>
                  <p className="mt-2 font-display font-semibold text-white text-lg print:text-black">Have it triaged before you sign.</p>
                  <p className="mt-2 text-sm text-slate-300 print:text-gray-700">
                    Most brand-deal redlines take 24–48 hours and cost a fraction of the deal value. Send a request and we'll tell you which of these ten apply to your contract — and which we'd add.
                  </p>
                  <Link to="/start" data-testid="checklist-cta-contact" className="mt-4 inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-6 py-3 rounded-full text-sm print:hidden">
                    Start a request <ArrowRight size={14} />
                  </Link>
                </div>

                <p className="mt-8 text-xs text-slate-500 leading-relaxed print:text-gray-600">
                  © {new Date().getFullYear()} Upload Counsel. This checklist is general information only and is not legal advice. Use of this checklist does not create an attorney-client relationship. The responsible attorney is identified in the website disclaimer. See uploadlawyer.com/legal/disclaimer.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
